import React, {useState} from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import {useNavigate, useParams} from 'react-router';
import ReadbookAuthorTooltip from "../../../../apps/policymaker/components/ReadbookAuthorTooltip.jsx";
import {Box, Chip, ListItemButton, Menu, MenuItem, Tooltip} from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import {formatTime} from '../../../../utils/formatTimestamp';

const API_BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

/**
 * FeedItemStack
 * Renders a feed item card.
 * - type === 'fintel': FINTEL report card (title, author, approval date, match score)
 * - type === 'corpus': corpus document hit (title, snippet)
 * - type === 'data_item': data item hit (title, snippet)
 * - Otherwise: IIR-style card with "Extracted from report" label and clickable points.
 */
const FeedItemStack = ({item, onClick, activeItemId, isActive: legacyActive, onMarkRead, onMarkUnread, onMarkHitRead, onMarkHitUnread}) => {
    const navigate = useNavigate();
    const {feedId} = useParams();
    const [contextMenu, setContextMenu] = useState(null);

    const isFintel = item?.type === 'fintel';
    const isCorpus = item?.type === 'corpus';
    const isDataItem = item?.type === 'data_item';

    if (isFintel) {
        const isActive = activeItemId ? String(activeItemId) === String(item.reportId) : false;
        const isUnread = item.read === false;

        // Convert cosine distance (0=identical → 1=orthogonal) to a 0–100 similarity %
        const similarityPct = (item.cosineDistance != null && item.cosineDistance >= 0)
            ? Math.round((1 - item.cosineDistance) * 100)
            : null;

        // Format approval time as "2:30 PM" from the ISO published timestamp
        const approvalTime = item.published ? formatTime(item.published, null) : null;

        const handleClick = () => {
            if (!item.reportId) return;
            // Pass the matched segment text as ?q= so FeedReportPreview can scroll
            // to and highlight the exact passage that caused this report to be selected.
            const seg = item.matchedSegmentText;
            // Use a longer prefix (150 chars) so the phrase is specific enough
            // to reliably land on body text rather than the report title.
            const qs = seg
                ? `?q=${encodeURIComponent(seg.slice(0, 150))}`
                : '';
            navigate(`${feedId}/${item.reportId}${qs}`, {relative: 'route'});
            if (isUnread && onMarkRead) onMarkRead(item.reportId);
        };

        const handleContextMenu = (e) => {
            e.preventDefault();
            setContextMenu({mouseX: e.clientX, mouseY: e.clientY});
        };

        const handleContextClose = () => setContextMenu(null);

        return (
            <>
                <Stack
                    direction="row"
                    onContextMenu={handleContextMenu}
                    sx={{
                        cursor: item.reportId ? 'pointer' : 'default',
                        backgroundColor: (legacyActive || isActive) ? '#eff1f9' : 'white',
                        borderLeft: (legacyActive || isActive)
                            ? '3px solid #3f51b5'
                            : isUnread ? '3px solid #1976d2' : '3px solid transparent',
                        borderBottom: '1px solid #f0f0f0',
                        '&:hover': item.reportId ? {backgroundColor: '#f5f5f5'} : undefined,
                        transition: 'background-color 0.15s ease',
                    }}
                    onClick={handleClick}
                    role={item.reportId ? 'button' : undefined}
                    tabIndex={item.reportId ? 0 : undefined}
                    onKeyDown={(e) => {
                        if (item.reportId && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            handleClick();
                        }
                    }}
                >
                    {/* Unread dot / read check */}
                    <Stack alignItems="center" justifyContent="flex-start"
                           sx={{pt: 1.75, px: 1.25, width: 24, flexShrink: 0}}>
                        {isUnread
                            ? <FiberManualRecordIcon sx={{fontSize: 9, color: '#1976d2'}}/>
                            : <CheckCircleOutlineIcon sx={{fontSize: 15, color: '#e0e0e0'}}/>
                        }
                    </Stack>

                    {/* ── Centre: badges + title + author ── */}
                    <Stack spacing={0.5} py={1.25} flexGrow={1} minWidth={0}>
                        {/* Status badges */}
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Chip
                                label="FINTEL"
                                size="small"
                                sx={{
                                    bgcolor: '#e8f5e9', color: '#2e7d32',
                                    fontWeight: 700, fontSize: '0.62rem',
                                    height: 17, borderRadius: '4px',
                                }}
                            />
                            <Chip
                                label="Approved"
                                size="small"
                                icon={<CheckCircleOutlineIcon style={{fontSize: 11}}/>}
                                sx={{
                                    bgcolor: '#e8f5e9', color: '#2e7d32',
                                    fontSize: '0.62rem', height: 17, borderRadius: '4px',
                                }}
                            />
                        </Stack>

                        {/* Title — bold when unread */}
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: isUnread ? 700 : 500,
                                lineHeight: 1.35,
                                color: isUnread ? 'text.primary' : 'text.secondary',
                                pr: 0.5,
                            }}
                        >
                            {item.title}
                        </Typography>

                        {/* Author */}
                        <ReadbookAuthorTooltip user={{
                            userDisplayName: item.userDisplayName,
                            displayName: item.userDisplayName,
                            headshot: item.headshot ? `${API_BASE}${item.headshot}` : undefined,
                            agency: item.agency,
                            office: item.office,
                            poc: item.poc,
                            contact: item.contact,
                            title: item.userTitle,
                            email: item.userEmail,
                            uid: item.userId,
                        }}/>
                    </Stack>

                    {/* ── Right column: score + time ── */}
                    <Stack
                        alignItems="flex-end"
                        justifyContent="flex-start"
                        spacing={0.75}
                        sx={{flexShrink: 0, pt: 1.25, pr: 1.5, minWidth: 72}}
                    >
                        {/* Similarity score */}
                        {similarityPct !== null && (
                            <Tooltip
                                title={`Semantic match: ${similarityPct}% similar to this feed's query (higher = more relevant)`}
                                placement="left"
                                arrow
                            >
                                <Stack alignItems="flex-end" spacing={0.25} sx={{cursor: 'default'}}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '0.58rem',
                                            color: 'text.disabled',
                                            lineHeight: 1,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                        }}
                                    >
                                        Match ↑
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: '0.85rem',
                                            lineHeight: 1.1,
                                            color: similarityPct >= 70 ? '#1565c0'
                                                 : similarityPct >= 50 ? '#e65100'
                                                 : '#9e9e9e',
                                        }}
                                    >
                                        {similarityPct}%
                                    </Typography>
                                    {/* Mini bar */}
                                    <Box sx={{
                                        width: 40,
                                        height: 3,
                                        borderRadius: 2,
                                        bgcolor: '#eeeeee',
                                        overflow: 'hidden',
                                    }}>
                                        <Box sx={{
                                            width: `${similarityPct}%`,
                                            height: '100%',
                                            bgcolor: similarityPct >= 70 ? '#1976d2'
                                                   : similarityPct >= 50 ? '#ef6c00'
                                                   : '#bdbdbd',
                                            borderRadius: 2,
                                        }}/>
                                    </Box>
                                </Stack>
                            </Tooltip>
                        )}

                        {/* Approval time */}
                        {approvalTime && (
                            <Stack alignItems="flex-end" spacing={0}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.6rem',
                                        color: 'text.disabled',
                                        lineHeight: 1.2,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.04em',
                                    }}
                                >
                                    Time
                                </Typography>
                                <Stack direction="row" alignItems="center" spacing={0.25}>
                                    <AccessTimeIcon sx={{fontSize: 10, color: 'text.disabled'}}/>
                                    <Typography
                                        variant="caption"
                                        color="text.disabled"
                                        sx={{fontSize: '0.68rem', whiteSpace: 'nowrap', lineHeight: 1.2}}
                                    >
                                        {approvalTime}
                                    </Typography>
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                </Stack>

                {/* Right-click context menu for toggling read/unread */}
                <Menu
                    open={contextMenu !== null}
                    onClose={handleContextClose}
                    anchorReference="anchorPosition"
                    anchorPosition={contextMenu ? {top: contextMenu.mouseY, left: contextMenu.mouseX} : undefined}
                >
                    {isUnread ? (
                        <MenuItem onClick={() => {
                            handleContextClose();
                            if (onMarkRead) onMarkRead(item.reportId);
                        }}>
                            Mark as read
                        </MenuItem>
                    ) : (
                        <MenuItem onClick={() => {
                            handleContextClose();
                            if (onMarkUnread) onMarkUnread(item.reportId);
                        }}>
                            Mark as unread
                        </MenuItem>
                    )}
                </Menu>
            </>
        );
    }

    // Corpus and data-item hits from the unified feed search
    if (isCorpus || isDataItem) {
        const scoreLabel = typeof item.score === 'number' ? Math.round(item.score * 100) : null;
        const isUnread = item.read === false;
        const openTarget = item.openTarget;

        const handleClick = () => {
            if (isUnread && onMarkHitRead) onMarkHitRead(item._hitKey);
            // Navigate into the right panel via URL query params — no modal, same container.
            if (isCorpus && openTarget?.docId != null) {
                navigate(
                    `${feedId}?previewType=corpus&docId=${openTarget.docId}&segmentIndex=${openTarget.segmentIndex ?? 0}`,
                    {relative: 'route'},
                );
            } else if (isDataItem && openTarget?.referenceId != null) {
                const params = new URLSearchParams({
                    previewType: 'data_item',
                    fileId: String(openTarget.referenceId),
                    canonicalFileId: String(openTarget.canonicalFileId ?? openTarget.referenceId),
                    fileName: item.title || '',
                });
                if (item.sentenceHit) {
                    params.set('sentenceHit', JSON.stringify(item.sentenceHit));
                }
                navigate(`${feedId}?${params.toString()}`, {relative: 'route'});
            }
        };

        const handleContextMenu = (e) => {
            e.preventDefault();
            setContextMenu({mouseX: e.clientX, mouseY: e.clientY});
        };

        const handleContextClose = () => setContextMenu(null);

        const badgeProps = isCorpus
            ? {label: 'CORPUS', color: '#7b1fa2', bg: '#f3e5f5', icon: <ArticleOutlinedIcon style={{fontSize: 10}}/>}
            : {label: 'MY DATA', color: '#0277bd', bg: '#e1f5fe', icon: <FolderOutlinedIcon style={{fontSize: 10}}/>};

        return (
            <>
                <Stack
                    direction="row"
                    onContextMenu={handleContextMenu}
                    sx={{
                        cursor: 'pointer',
                        borderLeft: isUnread ? '3px solid #1976d2' : '3px solid transparent',
                        borderBottom: '1px solid #f0f0f0',
                        '&:hover': {backgroundColor: '#f5f5f5'},
                        transition: 'background-color 0.15s ease',
                    }}
                    onClick={handleClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); }
                    }}
                >
                    {/* Unread dot / read check — mirrors FINTEL card */}
                    <Stack alignItems="center" justifyContent="flex-start"
                           sx={{pt: 1.75, px: 1.25, width: 24, flexShrink: 0}}>
                        {isUnread
                            ? <FiberManualRecordIcon sx={{fontSize: 9, color: '#1976d2'}}/>
                            : <CheckCircleOutlineIcon sx={{fontSize: 15, color: '#e0e0e0'}}/>
                        }
                    </Stack>

                    <Stack spacing={0.5} py={1.25} flexGrow={1} minWidth={0}>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Chip
                                label={badgeProps.label}
                                size="small"
                                sx={{
                                    bgcolor: badgeProps.bg,
                                    color: badgeProps.color,
                                    fontWeight: 700,
                                    fontSize: '0.62rem',
                                    height: 17,
                                    borderRadius: '4px',
                                }}
                            />
                        </Stack>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: isUnread ? 700 : 500,
                                lineHeight: 1.35,
                                color: isUnread ? 'text.primary' : 'text.secondary',
                                pr: 0.5,
                            }}
                        >
                            {item.title}
                        </Typography>
                        {item.snippet && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'text.disabled',
                                    fontSize: '0.7rem',
                                    lineHeight: 1.4,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                }}
                            >
                                …{item.snippet}…
                            </Typography>
                        )}
                    </Stack>

                    {scoreLabel !== null && (
                        <Stack
                            alignItems="flex-end"
                            justifyContent="flex-start"
                            sx={{flexShrink: 0, pt: 1.25, pr: 1.5, minWidth: 56}}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.58rem',
                                    color: 'text.disabled',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                }}
                            >
                                Match ↑
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.85rem',
                                    color: scoreLabel >= 70 ? '#1565c0' : scoreLabel >= 50 ? '#e65100' : '#9e9e9e',
                                }}
                            >
                                {scoreLabel}%
                            </Typography>
                        </Stack>
                    )}
                </Stack>

                {/* Right-click context menu for toggling read/unread — mirrors FINTEL card */}
                <Menu
                    open={contextMenu !== null}
                    onClose={handleContextClose}
                    anchorReference="anchorPosition"
                    anchorPosition={contextMenu ? {top: contextMenu.mouseY, left: contextMenu.mouseX} : undefined}
                >
                    {isUnread ? (
                        <MenuItem onClick={() => {
                            handleContextClose();
                            if (onMarkHitRead) onMarkHitRead(item._hitKey);
                        }}>
                            Mark as read
                        </MenuItem>
                    ) : (
                        <MenuItem onClick={() => {
                            handleContextClose();
                            if (onMarkHitUnread) onMarkHitUnread(item._hitKey);
                        }}>
                            Mark as unread
                        </MenuItem>
                    )}
                </Menu>
            </>
        );
    }

    // Safely parse points: item.points may be a JSON string or already an array
    let points = [];
    try {
        if (typeof item?.points === 'string') {
            points = JSON.parse(item.points) || [];
        } else if (Array.isArray(item?.points)) {
            points = item.points;
        }
    } catch (e) {
        console.warn('Failed to parse item.points', e);
        points = [];
    }

    return (
        <Stack
            bgcolor={'white'}
            sx={{
                // Remove interactive pointer/hover styles from parent; interactions are on list items now
                outline: legacyActive ? '2px solid #3f51b5' : 'none',
                backgroundColor: legacyActive ? '#eff1f9' : 'white'
            }}
        >
            <Stack spacing={1} p={2}>
                <Stack spacing={1}>
                    <Typography variant="h6" sx={{fontWeight: 600}}>
                        {item.title}
                    </Typography>
                </Stack>

                <Stack p={2} pt={1} pb={1.5}
                       outline={'1px solid #e0e0e0'}
                >
                    <Typography variant="subtitle2" color="text.secondary">
                        Extracted from report:
                    </Typography>
                    <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                        {item.reportTitle}
                    </Typography>
                    <Stack direction={'row'} alignItems={'center'} spacing={1} mt={1}>
                        <ReadbookAuthorTooltip user={{
                            userDisplayName: item.userDisplayName,
                            displayName: item.userDisplayName,
                            headshot: item.headshot,
                            agency: item.agency,
                            office: item.office,
                            poc: item.poc,
                            contact: item.contact,
                            title: item.userTitle,
                            email: item.userEmail,
                            uid: item.userId,
                        }}/>
                    </Stack>
                </Stack>
            </Stack>

            <Stack>
                {Array.isArray(points) && points.length > 0 && (
                    <List disablePadding>
                        {points.map((p, i) => {
                            // support multiple shapes: [uid, text], {uid, text}, or string
                            let uid, text;
                            if (Array.isArray(p)) {
                                uid = p[0];
                                text = p[1];
                            } else if (p && typeof p === 'object') {
                                uid = p.uid || p.id || p[0];
                                text = p.text || p.title || String(p);
                            } else {
                                text = String(p);
                            }

                            const pointIsActive = activeItemId ? String(activeItemId) === String(uid) : false;

                            const handlePointClick = (e) => {
                                // prevent any parent handlers
                                e.stopPropagation();
                                if (uid) {
                                    // navigate to uid (adjust route as needed)
                                    navigate(String(uid));
                                } else if (onClick) {
                                    // fallback: if point has no uid, treat clicking point as clicking whole item
                                    onClick();
                                }
                            };

                            const handleKeyDown = (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handlePointClick(e);
                                }
                            };

                            return (
                                <ListItemButton
                                    key={i}
                                    selected={pointIsActive}
                                    disableGutters
                                    button={!!uid || !!onClick}
                                    onClick={() => navigate(`${feedId}/${uid}`, {relative: 'route'})}
                                    onKeyDown={handleKeyDown}
                                    sx={{
                                        px: 2
                                    }}
                                >
                                    <ListItemText primary={text}/>
                                </ListItemButton>
                            )
                        })}
                    </List>
                )}
            </Stack>
        </Stack>
    );
};

export default FeedItemStack;
